// src/components/grammar/GrammarPillarRenderer.jsx
// ✨ UPDATED: Color theme changed from purple/blue gradients to a black, white, and royal blue theme.

import React, { useState, useEffect, useMemo } from 'react';
import { grammarStyles } from './grammarStyles';
import GrammarExerciseEngine from './GrammarExerciseEngine';
import StorytellerView from './visuals/StorytellerView.jsx';
import ProgressiveSectionReveal from './ProgressiveSectionReveal';
import InteractiveGrammarContent, { getIcon } from './InteractiveGrammarContent';
import { Info, Target, Grid, PlayCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// This component manages the content of the first tab (the story).
const StorySectionContent = ({
  pillar,
  storyHasStarted,
  storyIsComplete,
  storyKey,
  onStart,
  onRerun,
  onComplete,
  isMobile, // ✅ NEW: Receive isMobile prop
}) => {
    const { t } = useTranslation();

  // ✅ MODIFIED: Make styles responsive and theme-updated
  const styles = {
      container: {
        textAlign: 'center',
        padding: isMobile ? '0' : '3rem 2rem',
        background: '#FFFFFF',
        borderRadius: isMobile ? '0' : '1rem',
        border: isMobile ? 'none' : '1px solid #e2e8f0'
      },
      title: {
          color: '#1f2937',
          fontSize: isMobile ? '1.25rem' : '1.5rem',
          marginBottom: '1rem',
          fontWeight: '600',
          lineHeight: '1.5',
          letterSpacing: '0.01em'
      },
      description: {
          color: '#4b5563',
          marginBottom: '2rem',
          maxWidth: '500px',
          margin: '0 auto 2rem',
          fontSize: '1rem',
          lineHeight: '1.5',
          letterSpacing: '0.01em'
      },
      button: {
        background: '#000000',
        color: 'white',
        border: 'none',
        padding: isMobile ? '0.6rem 1.5rem' : '0.75rem 2rem',
        borderRadius: '2rem',
        fontSize: isMobile ? '1rem' : '1.1rem',
        fontWeight: '600',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.5rem',
        boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease'
      }
  };

  if (!storyHasStarted) {
    return (
      <div style={styles.container}>
        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📖</div>
        <h3 style={styles.title}>
          {t('grammar.renderer.seeInAction', { title: pillar.title })}
        </h3>
        <p style={styles.description}>
          {t('grammar.renderer.story.description')}
        </p>
        <button
          onClick={onStart}
          style={styles.button}
          onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
          onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
        >
          <PlayCircle size={20} /> {t('grammar.renderer.story.start')}
        </button>
      </div>
    );
  }

  return (
    <div>
      <StorytellerView
        key={storyKey}
        pillar={pillar}
        onStoryComplete={onComplete}
        isMobile={isMobile} // ✅ NEW: Pass isMobile to StorytellerView
      />
      {storyIsComplete && (
        <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
          <button
            onClick={onRerun}
            style={{
              background: '#f8fafc',
              color: '#475569',
              border: '1px solid #e2e8f0',
              padding: '0.5rem 1.5rem',
              borderRadius: '2rem',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
             onMouseEnter={(e) => e.target.style.borderColor = '#94a3b8'}
             onMouseLeave={(e) => e.target.style.borderColor = '#e2e8f0'}
          >
            {t('grammar.renderer.story.rerun')}
          </button>
        </div>
      )}
    </div>
  );
};


const GrammarPillarRenderer = ({
  pillar,
  userProgress,
  level,
  onOpenSectionsMenuRef // Ref to expose sections menu toggle
}) => {
  const { t } = useTranslation();
  const [currentSection, setCurrentSection] = useState(0);
 const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

console.log('📱 GrammarPillarRenderer - Window width:', window.innerWidth, 'isMobile:', isMobile); // DEBUG

  const [storyHasStarted, setStoryHasStarted] = useState(false);
  const [storyIsComplete, setStoryIsComplete] = useState(false);
  const [storyKey, setStoryKey] = useState(0);

  // ✅ MODIFIED: Create styles with a function to access isMobile
  // Styles are already black/white, so no theme changes needed here.
 const createStyles = (isMobile) => ({
  pillarCard: {
    ...grammarStyles().pillarCard,
    padding: isMobile ? '0' : '2rem',
    margin: isMobile ? '0' : 'auto',
    backgroundColor: '#FFFFFF',
    backgroundImage: `
      linear-gradient(0deg, transparent 24%, rgba(0, 0, 0, .005) 25%, rgba(0, 0, 0, .005) 26%, transparent 27%, transparent 74%, rgba(0, 0, 0, .005) 75%, rgba(0, 0, 0, .005) 76%, transparent 77%, transparent),
      linear-gradient(90deg, transparent 24%, rgba(0, 0, 0, .005) 25%, rgba(0, 0, 0, .005) 26%, transparent 27%, transparent 74%, rgba(0, 0, 0, .005) 75%, rgba(0, 0, 0, .005) 76%, transparent 77%, transparent)
    `,
    backgroundSize: '80px 80px',
    minHeight: '100vh',
    // Base multilingual typography, inherited by all prose children
    fontSize: '1rem',
    lineHeight: '1.5',
    letterSpacing: '0.01em'
  },
  pillarHeader: {
    ...grammarStyles().pillarHeader,
    padding: isMobile ? '0.5rem 0' : '2rem',
  },
  pillarTitle: grammarStyles().pillarTitle,
  pillarSubtitle: grammarStyles().pillarSubtitle,
});

  const styles = createStyles(isMobile);


  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleStoryComplete = () => {
    if (currentSection === 0) {
      setCurrentSection(1);
    }
    setStoryIsComplete(true);
  };

  const handleRerunStory = () => {
    setStoryIsComplete(false);
    setStoryKey(prevKey => prevKey + 1);
  };

  const grammarSections = useMemo(() => {
    const list = [];
    const explanation = pillar.explanation;
    if (!explanation) return [];

    if (explanation.summary)
      list.push({ title: 'Summary', type: 'summary', data: explanation.summary });
    if (explanation.categories)
      explanation.categories.forEach((cat) => list.push({ title: cat.name || cat.title || 'Section', type: 'category', data: cat }));
    if (explanation.forms)
      list.push({ title: 'Forms & Rules', type: 'forms', data: { forms: explanation.forms, byAgent: explanation.byAgent } });
    if (explanation.uses)
      list.push({ title: 'Usage', type: 'uses', data: { uses: explanation.uses, special_forms: explanation.forms?.special_forms } });

    return list;
  }, [pillar.explanation]);

  const allSections = useMemo(() => {
    const sections = [];

    sections.push({
      title: t('grammar.renderer.section.story'),
      content: (
        <StorySectionContent
          pillar={pillar}
          storyHasStarted={storyHasStarted}
          storyIsComplete={storyIsComplete}
          storyKey={storyKey}
          onStart={() => setStoryHasStarted(true)}
          onRerun={handleRerunStory}
          onComplete={handleStoryComplete}
          isMobile={isMobile} // ✅ NEW: Pass isMobile prop
        />
      )
    });

    sections.push({
      title: t('grammar.renderer.section.importance'),
      content: (
        <p style={{ color: '#374151', marginBottom: '1rem', fontSize: '1rem', lineHeight: '1.5', letterSpacing: '0.01em' }}>
          {pillar.strategicImportance}
        </p>
      )
    });

    grammarSections.forEach(gs => {
      sections.push({
        title: gs.title,
        content: (
          <InteractiveGrammarContent
            section={gs}
            isMobile={isMobile}
            userProgress={userProgress}
            pillar={pillar}
            level={level}
          />
        )
      });
    });

    if (pillar.practice && pillar.practice.length > 0) {
      sections.push({
        title: t('grammar.renderer.section.practice'),
        content: (
          <div>
            <p style={{ color: '#4b5563', marginBottom: '1.5rem' }}>
              {t('grammar.renderer.practice.description')}
            </p>
            <GrammarExerciseEngine
              embeddedExercises={pillar.practice.flatMap(group => group.tasks)}
              pillarId={pillar.id}
              userProgress={userProgress}
              level={level}
              onBack={() => console.log('Back from embedded practice')}
            />
          </div>
        )
      });
    }

    return sections;
  }, [pillar, grammarSections, isMobile, userProgress, level, storyHasStarted, storyIsComplete, storyKey, t]);

return (
  <div style={{
    ...styles.pillarCard,
    padding: isMobile ? '0' : styles.pillarCard.padding,
    backgroundColor: '#FFFFFF'
  }}>
    {!isMobile && (
  <div style={{
    ...styles.pillarHeader,
    position: 'relative'
  }}>
    {/* Top accent line */}
    <div style={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '4px',
      background: 'linear-gradient(90deg, #3A86FF 0%, #000000 50%, #3A86FF 100%)',
    }} />

    {/* Subtle grid pattern overlay */}
    <div style={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundImage: `
        linear-gradient(0deg, transparent 24%, rgba(255, 255, 255, 0.03) 25%, rgba(255, 255, 255, 0.03) 26%, transparent 27%, transparent 74%, rgba(255, 255, 255, 0.03) 75%, rgba(255, 255, 255, 0.03) 76%, transparent 77%, transparent),
        linear-gradient(90deg, transparent 24%, rgba(255, 255, 255, 0.03) 25%, rgba(255, 255, 255, 0.03) 26%, transparent 27%, transparent 74%, rgba(255, 255, 255, 0.03) 75%, rgba(255, 255, 255, 0.03) 76%, transparent 77%, transparent)
      `,
      backgroundSize: '50px 50px',
      opacity: 0.4,
      pointerEvents: 'none'
    }} />

    <div style={{ position: 'relative', zIndex: 1 }}>
      <h2 style={styles.pillarTitle}>{pillar.title}</h2>

      {/* Blue accent line under title */}
      <div style={{
        width: '80px',
        height: '4px',
        background: 'linear-gradient(90deg, #3A86FF 0%, #1a5dc7 100%)',
        borderRadius: '2px',
        margin: '0 auto 1rem',
        boxShadow: '0 2px 8px rgba(58, 134, 255, 0.4)'
      }} />

      <p style={styles.pillarSubtitle}>{pillar.subtitle}</p>
    </div>
  </div>
)}

      <ProgressiveSectionReveal
        sections={allSections}
        currentSection={currentSection}
        onSectionComplete={setCurrentSection}
        isMobile={isMobile}
        onOpenSectionsMenu={onOpenSectionsMenuRef}
      />
    </div>
  );
};

export default GrammarPillarRenderer;